import React, {
  CSSProperties,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import SocketContext from "../../pages/shared/SocketContext";
import { MessageItem, WhatsAppMessageListToastProps } from "./typings";
import { WhatsAppIcon } from "./WhatsAppIcon";
import { WhatsappButtonClose } from "./WhatsappButtonClose";
import { whatsappTheme } from "./constants";
import { TranslationService } from "../../services/TranslationService";
import TooltipComponent from "../../pages/shared/TooltipComponent";
import { SafeHtml } from "../SafeHtml";

/* -------------------------------- Helpers -------------------------------- */

const getTs = (x: any): number => {
  const v = x.updatedAt ?? x.UpdatedAt ?? x.createdAt ?? x.CreatedAt ?? 0;
  const n = new Date(v).getTime();
  return Number.isFinite(n) ? n : -Infinity;
};

// Función para truncar HTML preservando emojis
const truncateHTML = (html?: string, maxLength = 150): string => {
  if (!html) return "";
  // Si el HTML es más corto que el límite, devolverlo completo
  if (html.length <= maxLength) return html;
  // Truncar y agregar ellipsis
  return html.substring(0, maxLength - 1) + "…";
};

/** Latest sin ordenar toda la lista */
const pickLatest = (items?: MessageItem[]): MessageItem | undefined => {
  if (!items?.length) return undefined;
  let latest: MessageItem | undefined;
  let latestTs = -Infinity;
  for (const it of items) {
    const ts = getTs(it);
    if (ts > latestTs) {
      latest = it;
      latestTs = ts;
    }
  }
  // Si todos los timestamps fueron inválidos, caer al último elemento
  return latest ?? items[items.length - 1];
};

/* -------------------------------- Component ------------------------------- */

const WhatsAppMessageListToast: React.FC<WhatsAppMessageListToastProps> = ({
  items = [],
  openSignal,
  position = "right",
  bottom = 40,
  sideOffset = 40,
  buttonSize = 58,
  gap = 12,
  maxHeight = 360,
  style,
  headerText = "WhatsApp",
  closeOnOutsideClick = true,
  visible: visibleExternal,
  onClose,
  autoHideMs = 5000,
  onItemClick
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const { takeCall } = useContext(SocketContext);

  const [visible, setVisible] = useState(false);
  const [pinnedOpen, setPinnedOpen] = useState(false);

  // usar ReturnType para evitar conflictos Node vs browser
  const timerRef = useRef(null);
  const prevOpenSignal = useRef(openSignal);

  const listKey = useMemo(() => {
    if (!items?.length) return "empty";
    // ids + ts para invalidar aunque el texto sea igual
    return items.map((it) => `${it.id}:${getTs(it)}`).join(",");
  }, [items]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const latest = useMemo(() => pickLatest(items), [listKey]); // <- depende de listKey

  const latestKey = latest
    ? `${latest.id}|${latest.title}|${latest.description}|${getTs(latest)}`
    : null;
  const prevLatestKey = useRef<string | null>(null);

  const horizontal = sideOffset + buttonSize + gap;

  const containerStyle: CSSProperties = useMemo(
    () => ({
      ...whatsappTheme.messageContainer,
      ...(position === "right" ? { right: horizontal } : { left: horizontal }),
      ...style,
      bottom
    }),
    [bottom, position, horizontal, style]
  );

  const listStyle: CSSProperties = useMemo(
    () => ({ overflowY: "auto", maxHeight }),
    [maxHeight]
  );

  const itemStyle: CSSProperties = useMemo(
    () => ({
      padding: "10px 8px",
      borderRadius: 8,
      cursor: "pointer",
      userSelect: "none"
    }),
    []
  );

  const isVisible = (visibleExternal ?? visible) === true;

  /* -------------------------------- Handlers -------------------------------- */

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const handleClose = useCallback(() => {
    setVisible(false);
    setPinnedOpen(false);
    clearTimer();
    onClose?.();
  }, [clearTimer, onClose]);

  const navigateTo = useCallback(
    (item: MessageItem) => {
      takeCall();
      onItemClick?.(item);
      handleClose();
    },
    [handleClose, takeCall, onItemClick]
  );

  /* --------------------------------- Effects -------------------------------- */

  // 1) Señal externa "openSignal": fuerza apertura "pinned" y cancela timers
  useEffect(() => {
    if (openSignal !== prevOpenSignal.current) {
      clearTimer();
      setPinnedOpen(true);
      setVisible(true);
      prevOpenSignal.current = openSignal;
    }
  }, [openSignal, clearTimer]);

  // 2) Auto-hide cuando hay un nuevo latest y NO está pineado
  useEffect(() => {
    if (pinnedOpen) return;

    if (latestKey && latestKey !== prevLatestKey.current) {
      setVisible(true);
      clearTimer();
      timerRef.current = window.setTimeout(() => {
        setVisible(false);
        timerRef.current = null;
        onClose?.();
      }, autoHideMs);
    }
    prevLatestKey.current = latestKey;
  }, [latestKey, pinnedOpen, autoHideMs, onClose, clearTimer]);

  // 3) Limpieza del timer al desmontar
  useEffect(() => clearTimer, [clearTimer]);

  // 4) Click afuera para cerrar (solo cuando está visible)
  useEffect(() => {
    if (!closeOnOutsideClick || !isVisible) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        handleClose();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [closeOnOutsideClick, isVisible, handleClose]);

  // 5) Si se pinea, cancelar cualquier timer activo
  useEffect(() => {
    if (pinnedOpen) clearTimer();
  }, [pinnedOpen, clearTimer]);

  // 6) Modo "controlado" externo de visibilidad
  useEffect(() => {
    if (typeof visibleExternal === "boolean") {
      setVisible(visibleExternal);
      setPinnedOpen(visibleExternal);
      if (!visibleExternal) clearTimer();
    }
  }, [visibleExternal, clearTimer]);

  /* ---------------------------------- Render --------------------------------- */

  if (!isVisible) return null;

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label="Lista de mensajes de WhatsApp"
      style={containerStyle}
    >
      <div style={whatsappTheme.headerStyle}>
        <div style={whatsappTheme.leftHeaderStyle}>
          <WhatsAppIcon />
          <div style={whatsappTheme.headerTitleStyle}>{headerText}</div>
        </div>
        <WhatsappButtonClose onClick={handleClose} />
      </div>

      {pinnedOpen ? (
        <div style={listStyle}>
          {items.length === 0 ? (
            <div style={whatsappTheme.messageNNoChat}>
              {TranslationService.translate.NoDataToShow}
            </div>
          ) : (
            items.map((it) => (
              <div
                key={String(it.id)}
                onClick={() => navigateTo(it)}
                style={itemStyle}
                className="hoverMsgItem"
              >
                <div style={whatsappTheme.titleStyle} title={it.title}>
                  {it.title}
                </div>
                {it.description && (
                  <TooltipComponent title={it.description}>
                    <SafeHtml
                      html={truncateHTML(it.description, 50)}
                      style={{
                        ...whatsappTheme.messageDescription,
                        wordBreak: "break-all"
                      }}
                    />
                  </TooltipComponent>
                )}
              </div>
            ))
          )}
        </div>
      ) : latest ? (
        <div
          onClick={() => navigateTo(latest)}
          style={{ padding: "2px 2px 6px 2px", cursor: "pointer" }}
        >
          <div style={whatsappTheme.titleStyle} title={latest.title}>
            {latest.title}
          </div>
          {latest.description && (
            <TooltipComponent title={latest.description}>
              <SafeHtml
                html={truncateHTML(latest.description, 250)}
                style={{
                  ...whatsappTheme.messageDescription,
                  wordBreak: "break-all"
                }}
              />
            </TooltipComponent>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default WhatsAppMessageListToast;
